import type { EventView, HistoryItem, PayloadView, ShipmentDetail } from '@/lib/api/shipments';
import { carrierDate } from '@/lib/format/carrier-time';

// S07 · History (07 §3): GET /internal/shipments/:id/history (transitions,
// alerts, people's actions, notifications, diary lines) merged with the
// deliveries to Dokless, as the log's entries, newest first.

export type EntryKind = 'state' | 'alert' | 'notification' | 'person' | 'dokless' | 'delivery' | 'activity';
export type HistoryFilter = 'all' | EntryKind;
/** The segmented control (S07): «Attività» only shows when there is some. */
export const HISTORY_FILTERS: readonly HistoryFilter[] = [
  'all',
  'state',
  'alert',
  'notification',
  'person',
  'dokless',
  'delivery',
  'activity',
];

interface Base {
  key: string;
  at: Date;
}
export type HistoryEntry =
  | (Base & {
      kind: 'state';
      from: string | null;
      to: string;
      reason: string | null;
      who: string | null;
      /** Event ids the rule used. */
      evidence: string[];
    })
  | (Base & {
      kind: 'alert';
      action: 'opened' | 'updated' | 'closed';
      code: string;
      severity: string | null;
      params: Record<string, string>;
      closedReason: string | null;
    })
  | (Base & {
      kind: 'notification';
      notification: string;
      state: string;
      channel: string | null;
      reason: string | null;
      /** The message as sent (`details.rendered_text`, not in the API answer yet). */
      text: string | null;
    })
  | (Base & {
      kind: 'person';
      who: string | null;
      /** The person's name as the API resolves it. */
      whoName: string | null;
      /** Why, when the action asked for a reason. */
      reason: string | null;
      action: string;
      before: Record<string, unknown>;
      after: Record<string, unknown>;
    })
  | (Base & {
      kind: 'dokless' | 'activity';
      summary: string;
      who: string | null;
      readId: string | null;
      /** A Dokless request: the fingerprint (carrier and numbers) it carried. */
      fingerprint?: string | null;
    })
  | (Base & {
      kind: 'delivery';
      revision: number;
      payloadKind: string;
      state: string;
      status: number | null;
      nextCheckAt: string | null;
    });

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const str = (v: unknown) => (typeof v === 'string' && v ? v : null);

/** Transition evidence: the domain writes event ids; older rows may carry `{ events: [...] }`. */
export function evidenceIds(evidence: unknown): string[] {
  if (Array.isArray(evidence)) return evidence.filter((x): x is string => typeof x === 'string');
  const events = obj(evidence).events;
  return Array.isArray(events) ? events.filter((x): x is string => typeof x === 'string') : [];
}

/** Diary lines that repeat what another source already gives (transitions, notifications, deliveries). */
// `job_done` is the worker's routine bookkeeping (a recompute every few
// minutes) and `read_step`/`read_dispatched` the read's own progress (S05
// Letture has it): not actions, and they buried the real entries (verification).
const DUPLICATE_ACTIVITY = new Set([
  'transition',
  'notification',
  'delivery',
  'delivery_shadow',
  'job_done',
  'read_step',
  'read_dispatched',
]);
/** Diary kinds about Dokless's side: its requests, the alignment. */
const isDokless = (kind: string) => kind.startsWith('dokless') || kind.startsWith('intake');

/** The payload revisions that reached Dokless or were refused (shadow/pending ones are not history yet). */
const DELIVERY_STATES = new Set(['delivered', 'rejected', 'dead']);

export function buildEntries(
  items: readonly HistoryItem[],
  shipment: Pick<ShipmentDetail, 'transitions'>,
  payloads: readonly PayloadView[],
): HistoryEntry[] {
  const out: HistoryEntry[] = [];
  items.forEach((item, i) => {
    const at = new Date(item.at);
    if (!Number.isFinite(at.getTime())) return;
    const d = obj(item.details);
    const key = `${item.kind}-${i}`;
    switch (item.kind) {
      case 'transition': {
        const to = str(d.to_status) ?? '';
        const match = shipment.transitions.find((t) => t.at === item.at && t.to_status === to);
        const parsed = item.summary.includes(': ')
          ? item.summary.slice(item.summary.indexOf(': ') + 2)
          : null;
        out.push({
          key,
          at,
          kind: 'state',
          from: str(d.from_status),
          to,
          reason: match?.reason ?? parsed,
          who: item.who,
          evidence: evidenceIds(d.evidence ?? match?.evidence),
        });
        return;
      }
      case 'alert': {
        const m = /^alert \S+ (opened|updated|closed)(?: \((\w+)\))?(?::\s*(.*))?$/.exec(item.summary);
        const action = (m?.[1] ?? 'opened') as 'opened' | 'updated' | 'closed';
        const params = obj(d.params);
        out.push({
          key,
          at,
          kind: 'alert',
          action,
          code: str(d.code) ?? '',
          severity: m?.[2] ?? null,
          params: Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])),
          closedReason: action === 'closed' ? (m?.[3] ?? null) : null,
        });
        return;
      }
      case 'notification': {
        const kind = str(d.kind) ?? (item.summary.split(':')[0]?.trim() || 'text');
        out.push({
          key,
          at,
          kind: 'notification',
          notification: kind,
          state: str(d.state) ?? 'sent',
          channel: str(d.channel),
          reason: str(d.suppressed_reason),
          text: str(d.rendered_text),
        });
        return;
      }
      case 'audit':
        out.push({
          key,
          at,
          kind: 'person',
          who: item.who,
          whoName: str(d.who_name),
          reason: str(d.reason),
          action: item.summary,
          before: obj(d.before),
          after: obj(d.after),
        });
        return;
      case 'activity': {
        const kind = str(d.kind) ?? '';
        if (DUPLICATE_ACTIVITY.has(kind)) return;
        out.push({
          key,
          at,
          kind: isDokless(kind) ? 'dokless' : 'activity',
          summary: item.summary,
          who: item.who,
          readId: str(d.read_id),
          ...(kind === 'dokless_request' ? { fingerprint: str(d.fingerprint) } : {}),
        });
        return;
      }
    }
  });
  for (const p of payloads) {
    if (!DELIVERY_STATES.has(p.state)) continue;
    const at = new Date(p.sent_at ?? p.created_at);
    out.push({
      key: `delivery-${p.id}`,
      at,
      kind: 'delivery',
      revision: p.revision,
      payloadKind: p.kind,
      state: p.state,
      status: p.response_status,
      nextCheckAt: str(obj(p.response_body).next_check_at),
    });
  }
  // Newest first; at the same instant the API's order (stable sort).
  return out.sort((a, b) => b.at.getTime() - a.at.getTime());
}

export function countByKind(entries: readonly HistoryEntry[]): Record<HistoryFilter, number> {
  const n = Object.fromEntries(HISTORY_FILTERS.map((k) => [k, 0])) as Record<HistoryFilter, number>;
  for (const e of entries) n[e.kind]++;
  n.all = entries.length;
  return n;
}

export interface Evidence {
  kind: 'read' | 'ais' | 'event';
  readId?: string;
  eventId?: string;
  at: Date | null;
  /** AIS confirmation time as the carrier wrote it (`YYYY-MM-DDTHH:MM`), shown as is. */
  clock?: string;
}

/**
 * The «prove» of a transition: the reads that brought its events (one chip per
 * read, at the moment Plancia recorded it) and the AIS fixes that confirmed them.
 */
export function evidenceOf(ids: readonly string[], events: readonly EventView[]): Evidence[] {
  const out: Evidence[] = [];
  const reads = new Set<string>();
  const ais = new Set<string>();
  for (const id of ids) {
    const e = events.find((x) => x.id === id);
    if (!e) continue;
    if (e.read_id) {
      if (!reads.has(e.read_id)) {
        reads.add(e.read_id);
        out.push({ kind: 'read', readId: e.read_id, at: new Date(e.created_at) });
      }
    } else out.push({ kind: 'event', eventId: e.id, at: carrierDate(e.occurred_at) });
    if (Array.isArray(e.confirmed_by))
      for (const c of e.confirmed_by) {
        const source = obj(c).source;
        const when = str(obj(c).at);
        if (source !== 'ais' || !when || ais.has(when)) continue;
        ais.add(when);
        out.push({ kind: 'ais', at: null, clock: when });
      }
  }
  return out;
}

/** `2026-11-16T05:22` (carrier wall clock) → `05:22`; an ISO instant → local HH:mm. */
export function clockOf(value: string): string {
  const wall = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})$/.exec(value);
  if (wall) return `${wall[1]}:${wall[2]}`;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return value;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
